import { useCallback } from 'react';
import type { FC, ReactNode } from 'react';
import {
    Button,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    Text,
    useBreakpointValue,
    useDisclosure,
} from '@chakra-ui/react';
import { cx } from '@emotion/css';
import { useController, useForm } from 'react-hook-form';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import type { SystemStyleObject } from '@chakra-ui/react';
import type { FormState } from 'react-hook-form';
import type { MessageDescriptor } from 'react-intl';
import { CustomerNewsletterStatus } from '../../../bff-api/customers/model/CustomerNewsletterStatus';
import { useCustomerNewsletterSubscription } from '../../../bff-api/customers/useCustomerNewsletterSubscription';
import { useFooterEmailSignUpExclusionsHtml } from '../../../utils/api/footer-subscription';
import { regex } from '../../../utils/validation';
import { Content } from '../../content-asset/Content';
import styles from './Email.module.scss';
import type { CustomerNewsletterSubscriptionResponse } from '../../../bff-api/customers/model/CustomerNewsletterSubscriptionResponse';

const messages = defineMessages({
    emailPlaceholder: {
        id: 'footer.email.email_placeholder',
        defaultMessage: 'Email Address',
    },
    iframeTitle: {
        id: 'footer.email.email_title',
        defaultMessage: 'view exclusions',
    },
    emptyEmail: {
        id: 'footer.email.empty_email_error',
        defaultMessage: 'Email address is required',
    },
    invalidEmail: {
        id: 'footer.email.invalid_email_error',
        defaultMessage: 'Enter a valid email address',
    },
    accountExistsTitle: {
        id: 'footer.email.email_account_exists_title',
        defaultMessage: 'Nice to see you again.',
    },
    accountExistsMessage: {
        id: 'footer.email.email_account_exists',
        defaultMessage:
            "You're already on our email list, so you will continue to hear first about our new products and special offers.",
    },
    accountCreatedTitle: {
        id: 'footer.email.email_account_created_title',
        defaultMessage: 'Congratulations',
    },
    accountCreatedMessage: {
        id: 'footer.email.email_account_created',
        defaultMessage:
            "Congrats you're signed up! Check your inbox for your unique code to use on your next order.",
    },
});

export const Email: FC = () => {
    const intl = useIntl();
    const { isOpen, onClose, onOpen } = useDisclosure();

    const { data: signUpExclusionsHtml } = useFooterEmailSignUpExclusionsHtml();
    const { data, mutateAsync, isLoading } = useCustomerNewsletterSubscription();

    const form = useForm<{ email: string }>({ defaultValues: { email: '' } });
    const { field } = useController({
        name: 'email',
        control: form.control,
        rules: {
            required: intl.formatMessage(messages.emptyEmail),
            pattern: {
                value: regex.email,
                message: intl.formatMessage(messages.invalidEmail),
            },
        },
    });

    const handleOnSignUp = useCallback(async () => {
        const valid = await form.trigger();
        if (!valid) {
            return;
        }

        await mutateAsync({ ...form.getValues(), subscribe: true });
    }, [mutateAsync, form]);

    const promotionTextStyle: SystemStyleObject | undefined = useBreakpointValue(
        {
            base: {
                fontFamily: 'Lato,sans-serif',
                fontSize: '1rem',
                fontStyle: 'normal',
                fontWeight: '700',
                lineHeight: '120%',
                letterSpacing: '0.05rem',
            },
            xs: {
                fontFamily: 'Lato,sans-serif',
                fontSize: '0.875rem',
                fontStyle: 'normal',
                fontWeight: '700',
                lineHeight: '130%',
                letterSpacing: '0.06125rem',
            },
        },
        { fallback: 'xs' },
    );

    return (
        <div
            data-testid="footer-email-container"
            className="flex flex-col xs:gap-7 sm:gap-6 md:gap-8 md:max-lg:flex-row lg:gap-6"
        >
            <section>
                <Text color="slt-black.900" sx={promotionTextStyle}>
                    <FormattedMessage
                        id="footer.email.promotion"
                        defaultMessage="Sign up for emails and receive $15 off your order of $75 or more."
                    />
                    <Button
                        data-testid="footer.email.exclusions_button"
                        onClick={onOpen}
                        minWidth="unset"
                        height="unset"
                        border="0px"
                        borderRadius="0"
                        color="slt-black.900"
                        fontFamily="Lato,sans-serif"
                        fontSize="0.875rem"
                        fontWeight="400"
                        lineHeight="120%"
                        letterSpacing="0.04375rem"
                        textDecorationLine="underline"
                        backgroundColor="unset"
                        paddingInline="0px"
                        padding="0px"
                        paddingLeft="5px"
                        textTransform="unset"
                    >
                        <FormattedMessage
                            id="footer.email.exclusions"
                            defaultMessage="Exclusions apply"
                        />
                    </Button>
                </Text>
            </section>
            <div>
                <section className="flex h-max gap-4">
                    <Input
                        data-testid="footer.email.email_input"
                        placeholder={intl.formatMessage(messages.emailPlaceholder)}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        value={field.value}
                        name={field.name}
                        ref={field.ref}
                        _placeholder={{
                            color: 'slt-black.600',
                            fontFamily: 'Lato,sans-serif',
                            fontSize: '0.875rem',
                            fontWeight: 400,
                            lineHeight: '150%',
                            letterSpacing: '0.0175rem',
                        }}
                        borderRadius="0"
                        border="0px"
                        borderBottom="1px solid "
                        borderColor="slt-black.200"
                    />
                    <Button
                        data-testid="footer.email.signup_button"
                        onClick={handleOnSignUp}
                        isLoading={isLoading}
                        minWidth="unset"
                        height="unset"
                        padding="0.5rem 1rem"
                        border="1px solid #1E1E1E"
                        borderRadius="0"
                        color="slt-black.900"
                        fontFamily="Lato,sans-serif"
                        fontSize="0.875rem"
                        fontWeight="700"
                        lineHeight="150%"
                        letterSpacing="0.0175rem"
                        backgroundColor="unset"
                    >
                        <FormattedMessage id="footer.email.signup" defaultMessage="SIGN UP" />
                    </Button>
                </section>
                {renderResultMessage(data, form.formState)}
            </div>
            <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent data-testid="footer.email.exclusions_apply_modal_container">
                    <ModalCloseButton size="lg" />
                    <ModalBody>
                        <Content
                            className={cx(styles.footerEmailContainer, 'h-full w-full')}
                            serverHtml={signUpExclusionsHtml}
                        />
                    </ModalBody>
                </ModalContent>
            </Modal>
        </div>
    );
};

const renderResultMessage = (
    response?: CustomerNewsletterSubscriptionResponse,
    formState?: FormState<{ email: string }>,
): ReactNode => {
    if (!formState?.isValid && formState?.errors.email?.message) {
        return <EmailErrorMessage message={formState.errors.email.message} />;
    }

    if (!response) {
        return null;
    }

    switch (response.status) {
        case CustomerNewsletterStatus.SUBSCRIBED:
            return (
                <ResultSection
                    title={messages.accountCreatedTitle}
                    message={messages.accountCreatedMessage}
                />
            );
        case CustomerNewsletterStatus.ALREADY_SUBSCRIBED:
            return (
                <ResultSection
                    title={messages.accountExistsTitle}
                    message={messages.accountExistsMessage}
                />
            );
        default:
            return null;
    }
};

const ResultSection: FC<{
    title: MessageDescriptor;
    message: MessageDescriptor;
}> = ({ title, message }) => (
    <div className="mt-4">
        <Text textStyle="slt-former-body-text" fontWeight="700">
            <FormattedMessage {...title} />
        </Text>
        <Text textStyle="slt-former-body-text" className="mt-1">
            <FormattedMessage {...message} />
        </Text>
    </div>
);

const EmailErrorMessage: FC<{ message: string }> = ({ message }) => (
    <Text marginTop="4" color="slt-red-error">
        {message}
    </Text>
);
